import React from 'react';
import { Link } from 'react-router-dom';

const ACCENT = 'var(--accent)';

const MODULES = [
  {
    num: '1',
    name: 'Reaction & Decision',
    genre: 'All genres',
    duration: '~2 min',
    dimensions: ['Reaction Speed', 'Decision Quality', 'Consistency'],
    description: 'Three trial types run in sequence. Simple RT: a stimulus appears and the player responds as fast as possible — measures raw neural transmission speed. Choice RT: the player must select the correct response from two options — measures decision speed under cognitive load. Go/No-Go: a mixed stream of signals requires the player to respond or withhold — measures inhibitory control.',
    scored: [
      'Reaction Speed: mean simple RT in ms, percentile-ranked. Elite threshold: sub-155ms.',
      'Decision Quality: blended accuracy — choice RT accuracy (40%) + go/no-go accuracy (60%).',
      'Consistency: coefficient of variation across all valid RTs. Lower variance = higher score.',
    ],
    matters: 'Reaction Speed separates players at the margins of mechanical play. Consistency is what separates a 70th-percentile player from a 90th — most players are fast but unreliable.',
  },
  {
    num: '2',
    name: 'Aim Precision',
    genre: 'General + FPS',
    duration: '~1.5 min',
    dimensions: ['Aim Precision'],
    description: 'Targets appear at randomised positions. The player must click each target as quickly and accurately as possible. Both misses and partial hits are penalised. Isolates cursor control independent of in-game mechanics.',
    scored: [
      'Three sub-metrics blended: hit rate (50%), click RT on successful hits (30%), mean distance from target center (20%).',
      'Each sub-metric is independently percentile-ranked before blending.',
    ],
    matters: 'Hit rate alone misses the precision ceiling. The three-metric blend captures both the accuracy and the quality of the accuracy.',
  },
  {
    num: '2b',
    name: 'Target Sequencing',
    genre: 'MOBA only',
    duration: '~1.5 min',
    dimensions: ['Prioritization'],
    description: 'Numbered targets appear and must be clicked in the correct order as fast as possible within a time window. Sequences increase in complexity across rounds. Tests rapid processing and execution of ordered information — a proxy for ability combo execution and multi-target decision-making.',
    scored: [
      'Completion rate across all rounds (70%) + mean inter-click RT on completed rounds (30%).',
      'Both sub-metrics independently percentile-ranked before blending.',
    ],
    matters: 'In MOBA play, the ability to execute priority-ordered sequences under time pressure maps directly to ability rotation, last-hit timing, and multi-target engagement.',
  },
  {
    num: '3',
    name: 'Target Tracking',
    genre: 'All genres',
    duration: '~1.5 min',
    dimensions: ['Tracking Accuracy'],
    description: 'A target moves in a Lissajous curve pattern — a figure-eight that produces unpredictable-feeling motion without following a simple loop. The player must keep the cursor on the target continuously. Requires constant micro-adjustments rather than predictable intercept strategies.',
    scored: [
      'Time-on-target proportion (60%) + mean normalised distance from target center (40%).',
      'Both sub-metrics independently percentile-ranked before blending.',
    ],
    matters: 'Measures smooth pursuit motor control — the ability to follow a moving object. More predictive of sustained combat performance than static aim tests.',
  },
  {
    num: '3b',
    name: 'Flicker Detection',
    genre: 'FPS only',
    duration: '~1.5 min',
    dimensions: ['Flicker Speed'],
    description: 'Targets appear for a 500ms window at random positions. 30 trials. The short window makes pre-aiming impossible — isolates the ability to react, acquire, and execute within a brief opportunity. Replicates the flick-shot scenario in FPS titles.',
    scored: [
      'Hit rate across 30 trials (60%) + mean RT on successful hits (40%).',
      'Elite threshold: hit rate ≥0.90, mean RT ≤120ms.',
    ],
    matters: 'The closest in-test replication of the FPS flick-shot — a target that will disappear if you don\'t act.',
  },
  {
    num: '4',
    name: 'Working Memory',
    genre: 'General + MOBA',
    duration: '~1.5 min',
    dimensions: ['Working Memory'],
    description: 'A 3×3 grid. A sequence of cells is highlighted in order, then cleared. The player must reproduce the sequence from memory by clicking the cells in the correct order. Sequence length increases from 3 to 6 cells across rounds.',
    scored: [
      'Proportion of cells recalled in correct order across all rounds, percentile-ranked.',
      '50th percentile: 46% accuracy. 90th percentile: 82%. 99th percentile: 95%.',
    ],
    matters: 'Working memory capacity correlates with the ability to track multiple entities simultaneously — teammates, enemies, objectives, cooldowns.',
  },
  {
    num: '5',
    name: 'Composure',
    genre: 'All genres',
    duration: '~2 min',
    dimensions: ['Composure'],
    description: 'A Flanker task: the player responds to a central stimulus, with flanking distractors that are congruent or incongruent. Two phases — baseline (no pressure) and distraction (incongruent flankers, faster pace, audio cues). The key measurement is the performance delta between phases.',
    scored: [
      'Composure delta = accuracy retention (60%) + RT retention (40%).',
      'Accuracy retention: distraction accuracy as proportion of baseline accuracy.',
      'RT retention: baseline RT / distraction RT — higher means less slowdown under pressure.',
      '50th percentile: ~87/100. 99th percentile: ~111 (some players improve under distraction).',
    ],
    matters: 'Consistently the strongest predictor of clutch performance in the dataset. The distribution is wide — high-composure players are rare and valuable.',
  },
  {
    num: '6',
    name: 'Tilt Resistance',
    genre: 'All genres',
    duration: '~2 min',
    dimensions: ['Risk Calibration'],
    description: 'A Bet Under Pressure task. The player completes a decision task and makes a bet each round. A baseline phase establishes natural calibration. Then a failure phase engineers a run of bad outcomes. A recovery phase follows. Measures whether decision-making and risk calibration degrade or recover after sustained failure.',
    scored: [
      'Recovery accuracy after the failure phase (60%) + rational bet rate across all phases (40%).',
      'Rational bet rate: proportion of rounds where the bet matched the player\'s recent performance signal.',
      '50th percentile: 59/100. 99th percentile: 100/100.',
    ],
    matters: 'The most difficult dimension to train. Players who maintain calibrated decision-making after a bad streak are significantly more likely to stabilise a round. The distribution on this dimension is wider than any other.',
  },
];

const TIERS = [
  { range: '90–100', tier: 'Elite',      desc: 'Top 10% of the competitive gamer population on this dimension.' },
  { range: '75–89',  tier: 'Top 25%',    desc: 'Comfortably above the median. A meaningful strength.' },
  { range: '60–74',  tier: 'Top 40%',    desc: 'Above average. Not a liability, not a standout.' },
  { range: '50–59',  tier: 'Above Avg',  desc: 'Slightly above the population median.' },
  { range: '35–49',  tier: 'Developing', desc: 'Below the median. A trainable gap.' },
  { range: '0–34',   tier: 'Rookie',     desc: 'Significantly below median. Most players here have never trained this dimension.' },
];

const NOT_MEASURED = [
  { item: 'Game knowledge and map awareness', why: 'These are domain-specific skills built through game time. The combine measures the cognitive substrate — the processing speed and memory capacity that game knowledge runs on.' },
  { item: 'Raw mechanical skill in-game', why: 'The test uses a standardised canvas environment, not your game. Aim precision correlates with in-game aim but is not identical to it. Hardware, sensitivity, and muscle memory are not tested.' },
  { item: 'Team communication and leadership', why: 'Leadership and communication require separate assessment. The combine measures individual cognitive performance under controlled conditions.' },
  { item: 'Potential or ceiling', why: 'The combine measures current cognitive state, not ceiling. Most dimensions are trainable. A low score today is a gap, not a verdict.' },
];

export default function Science() {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}>

      {/* Hero */}
      <section style={{ maxWidth: 760, margin: '0 auto', padding: '80px 24px 60px', textAlign: 'center', borderBottom: '1px solid var(--border)' }}>
        <p style={{ fontSize: 10, letterSpacing: 4, textTransform: 'uppercase', color: 'var(--accent)', fontWeight: 700, marginBottom: 16 }}>Methodology</p>
        <h1 style={{ fontSize: 42, fontWeight: 900, letterSpacing: '-0.03em', margin: '0 0 16px', lineHeight: 1.1 }}>How the combine works</h1>
        <p style={{ fontSize: 17, color: 'var(--text-secondary)', lineHeight: 1.7, maxWidth: 560, margin: '0 auto 32px' }}>
          6 test modules. 8 cognitive dimensions. Every score is percentile-ranked against a competitive gamer population. Here is exactly what each module measures and how scores are calculated.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '24px 40px', fontSize: 13, color: 'var(--text-muted)' }}>
          {[['10 min', 'total test time'], ['8', 'dimensions measured'], ['18', 'archetype classifications'], ['3', 'genre variants']].map(([val, label]) => (
            <span key={label}><strong style={{ color: 'var(--text-primary)' }}>{val}</strong> {label}</span>
          ))}
        </div>
      </section>

      {/* Modules */}
      <section style={{ maxWidth: 760, margin: '0 auto', padding: '60px 24px' }}>
        <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 6 }}>Test modules</h2>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 36 }}>Each module isolates a specific cognitive sub-system. Modules run sequentially. No module reuses another module's data.</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {MODULES.map((m) => (
            <div key={m.num} style={{ border: '1px solid var(--border)', borderRadius: 12, padding: '24px', background: 'var(--bg-card)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <p style={{ fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--accent)', fontWeight: 700, margin: '0 0 4px' }}>Module {m.num}</p>
                  <h3 style={{ fontSize: 18, fontWeight: 800, margin: 0 }}>{m.name}</h3>
                </div>
                <div style={{ textAlign: 'right', fontSize: 12, color: 'var(--text-muted)' }}>
                  <div>{m.genre}</div>
                  <div>{m.duration}</div>
                </div>
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
                {m.dimensions.map((d) => (
                  <span key={d} style={{ padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.2)', color: 'var(--accent)' }}>{d}</span>
                ))}
              </div>

              <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: 16 }}>{m.description}</p>

              <div style={{ background: 'var(--bg-primary)', borderRadius: 8, padding: '14px 16px', marginBottom: 16 }}>
                <p style={{ fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--text-muted)', fontWeight: 700, margin: '0 0 10px' }}>How it's scored</p>
                <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {m.scored.map((line, i) => (
                    <li key={i} style={{ display: 'flex', gap: 8, fontSize: 13, color: 'var(--text-muted)' }}>
                      <span style={{ color: 'var(--accent)', fontWeight: 700, flexShrink: 0 }}>→</span>
                      {line}
                    </li>
                  ))}
                </ul>
              </div>

              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14 }}>
                <p style={{ fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--text-muted)', fontWeight: 700, margin: '0 0 6px' }}>Why it matters</p>
                <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.7, margin: 0 }}>{m.matters}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Population norms */}
      <section style={{ maxWidth: 760, margin: '0 auto', padding: '60px 24px', borderTop: '1px solid var(--border)' }}>
        <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 16 }}>Population norms and percentile ranking</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 36 }}>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.75, margin: 0 }}>Each raw metric is converted to a percentile score (0–100) using norm tables derived from cognitive science literature on young adult (18–30) gamer populations. Calibrated to reflect the gaming-age population, not the general population.</p>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.75, margin: 0 }}>Norm tables will recalibrate as real NeuroRank data accumulates. Current tables are conservative: a player who belongs in the 80th percentile will score in the 80th range, not inflated.</p>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.75, margin: 0 }}>Overall score is a weighted average of all dimension scores for the selected genre. Dimensions with the widest population variance carry more weight because they carry more signal.</p>
        </div>

        <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
          <div className="science-tier-header" style={{ display: 'grid', gridTemplateColumns: '90px 110px 1fr', padding: '10px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-card)' }}>
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-muted)' }}>Score</span>
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-muted)' }}>Tier</span>
            <span className="science-tier-desc" style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-muted)' }}>What it means</span>
          </div>
          {TIERS.map((row, i) => (
            <div key={row.range} className="science-tier-row" style={{ display: 'grid', gridTemplateColumns: '90px 110px 1fr', padding: '12px 16px', borderBottom: i < TIERS.length - 1 ? '1px solid var(--border)' : 'none', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)' }}>
              <span style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{row.range}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)' }}>{row.tier}</span>
              <span className="science-tier-desc" style={{ fontSize: 13, color: 'var(--text-muted)' }}>{row.desc}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Archetype classification */}
      <section style={{ maxWidth: 760, margin: '0 auto', padding: '60px 24px', borderTop: '1px solid var(--border)' }}>
        <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 16 }}>How archetypes are assigned</h2>
        <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.75, marginBottom: 28 }}>
          After scoring, each player's dimension profile is compared against 18 archetype templates — 6 per genre (General, FPS, MOBA). Each archetype is defined by a signature pattern: which dimensions are elevated, which are average, and which are suppressed. The classifier scores similarity between the player's profile and each template and assigns the closest match.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12, marginBottom: 20 }}>
          {[
            { name: 'The AWPer',          signal: 'Elevated Flicker Speed + Reaction Speed. Average Composure.' },
            { name: 'The Entry Fragger',  signal: 'Elevated Reaction Speed + Aim Precision. Composure below median.' },
            { name: 'The Strategist',     signal: 'Elevated Decision Quality + Working Memory. Average Raw Speed.' },
            { name: 'The Clutch Reactor', signal: 'Elevated Composure + Risk Calibration. Average Reaction Speed.' },
          ].map((ex) => (
            <div key={ex.name} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px', background: 'var(--bg-card)' }}>
              <p style={{ fontSize: 14, fontWeight: 700, margin: '0 0 4px' }}>{ex.name}</p>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>{ex.signal}</p>
            </div>
          ))}
        </div>
      </section>

      {/* What it doesn't measure */}
      <section style={{ maxWidth: 760, margin: '0 auto', padding: '60px 24px', borderTop: '1px solid var(--border)' }}>
        <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 24 }}>What the combine does not measure</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {NOT_MEASURED.map((row) => (
            <div key={row.item} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px', background: 'var(--bg-card)' }}>
              <p style={{ fontSize: 14, fontWeight: 700, margin: '0 0 4px' }}>{row.item}</p>
              <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0, lineHeight: 1.65 }}>{row.why}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section style={{ maxWidth: 760, margin: '0 auto', padding: '60px 24px 80px', borderTop: '1px solid var(--border)', textAlign: 'center' }}>
        <h2 style={{ fontSize: 30, fontWeight: 900, marginBottom: 12 }}>Take the combine</h2>
        <p style={{ fontSize: 15, color: 'var(--text-secondary)', marginBottom: 32 }}>10 minutes. Free. Full dimension breakdown, archetype classification, and AI scouting report.</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center', marginBottom: 24 }}>
          <Link to="/?genre=general" style={{ padding: '13px 28px', borderRadius: 10, fontSize: 14, fontWeight: 800, background: 'var(--accent)', color: '#000', textDecoration: 'none' }}>General Combine →</Link>
          <Link to="/?genre=fps"     style={{ padding: '13px 28px', borderRadius: 10, fontSize: 14, fontWeight: 700, border: '1px solid var(--border)', color: 'var(--text-secondary)', textDecoration: 'none' }}>FPS Combine →</Link>
          <Link to="/?genre=moba"    style={{ padding: '13px 28px', borderRadius: 10, fontSize: 14, fontWeight: 700, border: '1px solid var(--border)', color: 'var(--text-secondary)', textDecoration: 'none' }}>MOBA Combine →</Link>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          Coaches:{' '}
          <Link to="/for-coaches" style={{ color: 'var(--accent)', textDecoration: 'none' }}>see the scouting dashboard →</Link>
        </p>
      </section>
    </div>
  );
}
